import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow } from '@/components/ui/ModuleScreenLoading'
import {
  deleteQueuedJob,
  getQueuedJobs,
  resetQueuedJob,
} from '../services/jobsService'
import { sendAnalytics } from '@/lib/utils/analytics'
import { secondaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import type { QueuedJob } from '../types'

function formatDateTime(value: string | number | undefined | null): string {
  if (!value) return ''
  const ts = typeof value === 'number' ? value * 1000 : Date.parse(value)
  const date = new Date(ts)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

function formatReservedAt(
  reservedAt: number | string | null | undefined,
  attempts: number | null | undefined,
  currentTimeSec: number
): string {
  let reservedStr = ''
  if (reservedAt) {
    const formattedDate = formatDateTime(reservedAt)
    const tsSec = typeof reservedAt === 'number' ? reservedAt : Math.floor(Date.parse(reservedAt) / 1000)
    if (!Number.isNaN(tsSec) && tsSec > 0) {
      const execMin = Math.round(((currentTimeSec - tsSec) / 60) % 60)
      reservedStr = `${formattedDate} executing time ${execMin} min`
    } else {
      reservedStr = formattedDate
    }
  }
  const attemptsStr = attempts ? ` (${attempts})` : ''
  return `${reservedStr}${attemptsStr}`
}

export function QueuedJobsPage() {
  const [jobs, setJobs] = useState<QueuedJob[]>([])
  const [currentTimeSec, setCurrentTimeSec] = useState<number>(() => Date.now() / 1000)
  const [loading, setLoading] = useState(false)
  const [actionBusy, setActionBusy] = useState(false)
  const mountedRef = useRef(false)
  const actionInFlight = useRef(false)

  const load = useCallback(async () => {
    setLoading(true)
    setCurrentTimeSec(Date.now() / 1000)
    try {
      setJobs(await getQueuedJobs())
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load()
  }, [load])

  const runWithLock = useCallback(
    async (fn: () => Promise<void>, successMessage: string) => {
      if (actionInFlight.current || !jobs.length) return
      actionInFlight.current = true
      setActionBusy(true)
      try {
        await fn()
        showSuccess(successMessage)
        await load()
      } catch (error) {
        showApiError(error)
      } finally {
        actionInFlight.current = false
        setActionBusy(false)
      }
    },
    [jobs.length, load],
  )

  const handleResetAll = () => {
    sendAnalytics('click', 'queue job retry', '')
    void runWithLock(() => resetQueuedJob(), 'Job Reset Successfully')
  }

  const handleDeleteAll = () => {
    sendAnalytics('click', 'queue job delete', '')
    void runWithLock(() => deleteQueuedJob(), 'Job Deleted Successfully')
  }

  const handleDeleteOne = (id: number) => {
    if (actionInFlight.current) return
    void (async () => {
      actionInFlight.current = true
      setActionBusy(true)
      try {
        sendAnalytics('click', 'queue job delete', '')
        await deleteQueuedJob(id)
        showSuccess('Job Deleted Successfully')
        await load()
      } catch (error) {
        showApiError(error)
      } finally {
        actionInFlight.current = false
        setActionBusy(false)
      }
    })()
  }

  const controlsDisabled = loading || actionBusy || jobs.length === 0

  return (
    <PageShell title="QUEUED JOBS">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/jobs/failed"
          className={
            actionBusy
              ? `${secondaryEntityActionButtonClass} opacity-50 pointer-events-none`
              : secondaryEntityActionButtonClass
          }
          aria-disabled={actionBusy}
        >
          Failed Jobs
        </Link>
        <button
          type="button"
          onClick={handleResetAll}
          disabled={controlsDisabled}
          className={secondaryEntityActionButtonClass}
        >
          Reset All
        </button>
        <button
          type="button"
          onClick={handleDeleteAll}
          disabled={controlsDisabled}
          className={secondaryEntityActionButtonClass}
        >
          Delete All
        </button>
      </div>

      {actionBusy && (
        <p className="text-sm text-slate-500" role="status">
          Processing…
        </p>
      )}

      <div className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm mt-4">
        <div className="overflow-x-auto">
          <table className={applicationStyles.listingTable}>
            <thead>
              <tr>
                <th>ID #</th>
                <th>Queue</th>
                <th>Created At</th>
                <th>Reserved At</th>
                <th>Available At</th>
                <th>Payload</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
            {loading ? (
              <ListingTableLoadingRow colSpan={7} />
            ) : jobs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  No queued jobs.
                </td>
              </tr>
            ) : (
              jobs.map((job) => (
                <tr key={job.id}>
                  <td className="font-medium text-slate-900">{job.id}</td>
                  <td className="text-slate-700">{job.queue}</td>
                  <td className="text-slate-600 whitespace-nowrap">
                    {formatDateTime(job.created_at)}
                  </td>
                  <td className="text-slate-600 whitespace-nowrap">
                    {formatReservedAt(job.reserved_at, job.attempts, currentTimeSec)}
                  </td>
                  <td className="text-slate-600 whitespace-nowrap">
                    {job.available_at ? formatDateTime(job.available_at) : ''}
                  </td>
                  <td>
                    <textarea
                      readOnly
                      value={job.payload}
                      className="w-64 min-h-[5rem] max-w-md font-mono text-xs p-2 border border-slate-200 rounded bg-slate-50"
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleDeleteOne(job.id)}
                      disabled={actionBusy}
                      className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      aria-label="Delete"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </PageShell>
  )
}
